import type {
  IncidentExportPartnerSummary,
  IncidentExportRecord,
  PartnerDeliveryRecord,
} from "@/lib/api/client"

export const INPUT_CLASS =
  "w-full rounded-2xl border border-border/70 bg-background/85 px-3 py-2 text-sm shadow-xs outline-none transition-colors focus:border-primary/50"

export const TEXTAREA_CLASS = `${INPUT_CLASS} min-h-28 resize-y`

export type PartnerDeliveryChainPosture =
  | "delivered"
  | "dead_letter"
  | "in_flight"
  | "replayed"
  | "retry_queue"

export interface PartnerDeliveryChainSummary {
  callbackVisibleCount: number
  deadLetterCount: number
  deliveredCount: number
  deliveries: PartnerDeliveryRecord[]
  entitlementKeys: string[]
  exportIds: string[]
  externalDeliveryIds: string[]
  governanceLabels: string[]
  id: string
  latestActivityAt?: string | null
  latestDelivery: PartnerDeliveryRecord
  latestStatus?: string | null
  partnerKeys: string[]
  pendingRetryCount: number
  posture: PartnerDeliveryChainPosture
  purposeLabels: string[]
  regionKeys: string[]
  replayCount: number
  rootId: string
}

export function titleizeToken(value: string | null | undefined) {
  const normalized = String(value ?? "")
    .trim()
    .toLowerCase()
    .replace(/[_-]+/g, " ")

  if (!normalized) {
    return "Unknown"
  }

  return normalized
    .split(/\s+/)
    .filter(Boolean)
    .map((segment) => segment.charAt(0).toUpperCase() + segment.slice(1))
    .join(" ")
}

export function formatDateTime(value: string | null | undefined) {
  if (!value) {
    return "Not recorded"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  return parsed.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  })
}

export function formatMaybeDate(value: string | null | undefined) {
  return value ? formatDateTime(value) : "Not recorded"
}

export function formatRelativeTime(value: string | null | undefined) {
  if (!value) {
    return "No recent activity"
  }

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) {
    return value
  }

  const diffMs = parsed.getTime() - Date.now()
  const absMinutes = Math.round(Math.abs(diffMs) / 60000)

  if (absMinutes < 1) {
    return diffMs >= 0 ? "in less than a minute" : "just now"
  }

  if (absMinutes < 60) {
    return diffMs >= 0 ? `in ${absMinutes}m` : `${absMinutes}m ago`
  }

  const absHours = Math.round(absMinutes / 60)
  if (absHours < 24) {
    return diffMs >= 0 ? `in ${absHours}h` : `${absHours}h ago`
  }

  const absDays = Math.round(absHours / 24)
  return diffMs >= 0 ? `in ${absDays}d` : `${absDays}d ago`
}

export function shortId(value: string | null | undefined, width = 8) {
  const text = String(value ?? "").trim()
  if (!text) {
    return "n/a"
  }

  return text.length > width ? text.slice(0, width) : text
}

export function safeJsonStringify(value: unknown) {
  try {
    return JSON.stringify(value, null, 2)
  } catch {
    return String(value)
  }
}

export function matchesFilterText(query: string | undefined, values: unknown[]) {
  const normalized = String(query ?? "").trim().toLowerCase()
  if (!normalized) {
    return true
  }

  return values
    .map((value) => {
      if (value === null || value === undefined) {
        return ""
      }

      return typeof value === "string" ? value : safeJsonStringify(value)
    })
    .join(" ")
    .toLowerCase()
    .includes(normalized)
}

export function getPartnerSummary(record?: IncidentExportRecord | null) {
  const summary = record?.delivery?.partnerSummary

  return (
    summary ?? {
      acknowledgedCount: 0,
      deadLetterCount: 0,
      deliveredCount: 0,
      latestActivityAt: null,
      latestStatus: "not_started",
      liveCount: 0,
      pendingRetryCount: 0,
      submittedCount: 0,
      totalDeliveries: 0,
    }
  ) as IncidentExportPartnerSummary
}

function getLatestActivity(delivery: PartnerDeliveryRecord) {
  return (
    delivery.execution?.lastAttemptAt
    || delivery.execution?.lastCallbackAt
    || delivery.updatedAt
    || delivery.createdAt
    || null
  )
}

function deriveChainPosture(
  deliveries: PartnerDeliveryRecord[],
  latestDelivery: PartnerDeliveryRecord
): PartnerDeliveryChainPosture {
  if (
    latestDelivery.replayedFromDeliveryId
    || deliveries.some((delivery) => Boolean(delivery.replayedFromDeliveryId))
  ) {
    return "replayed"
  }

  if (
    latestDelivery.status === "dead_letter"
    || deliveries.some(
      (delivery) =>
        delivery.status === "dead_letter" || Boolean(delivery.execution?.deadLetteredAt)
    )
  ) {
    return "dead_letter"
  }

  if (
    latestDelivery.status === "retryable"
    || deliveries.some(
      (delivery) =>
        delivery.status === "retryable" || Boolean(delivery.execution?.nextRetryAt)
    )
  ) {
    return "retry_queue"
  }

  if (
    latestDelivery.status === "delivered"
    && deliveries.every((delivery) => delivery.status === "delivered")
  ) {
    return "delivered"
  }

  return "in_flight"
}

function resolveChainRootId(
  delivery: PartnerDeliveryRecord,
  deliveriesById: Map<string, PartnerDeliveryRecord>
) {
  let current = delivery
  const visited = new Set<string>()

  while (current.replayedFromDeliveryId) {
    if (visited.has(current.id)) {
      break
    }

    visited.add(current.id)
    const previous = deliveriesById.get(current.replayedFromDeliveryId)
    if (!previous) {
      break
    }

    current = previous
  }

  return current.id
}

export function buildPartnerDeliveryChainSummaries(
  deliveries: PartnerDeliveryRecord[]
) {
  const deliveriesById = new Map(deliveries.map((delivery) => [delivery.id, delivery]))
  const groups = new Map<string, PartnerDeliveryRecord[]>()

  for (const delivery of deliveries) {
    const rootId = resolveChainRootId(delivery, deliveriesById)
    const existing = groups.get(rootId) ?? []
    existing.push(delivery)
    groups.set(rootId, existing)
  }

  return Array.from(groups.entries())
    .map(([rootId, chainDeliveries]) => {
      const orderedByCreated = chainDeliveries
        .slice()
        .sort(
          (left, right) =>
            new Date(left.createdAt ?? 0).getTime()
            - new Date(right.createdAt ?? 0).getTime()
        )
      const orderedByRecent = chainDeliveries
        .slice()
        .sort(
          (left, right) =>
            new Date(getLatestActivity(right) ?? 0).getTime()
            - new Date(getLatestActivity(left) ?? 0).getTime()
        )
      const latestDelivery = orderedByRecent[0]
      const governanceLabels = orderedByCreated.flatMap(
        (delivery) => delivery.governance?.governanceLabels ?? []
      )

      return {
        callbackVisibleCount: orderedByCreated.filter(
          (delivery) =>
            Boolean(delivery.execution?.lastCallbackAt)
            || Boolean(delivery.execution?.callbackStatus)
        ).length,
        deadLetterCount: orderedByCreated.filter(
          (delivery) =>
            delivery.status === "dead_letter"
            || Boolean(delivery.execution?.deadLetteredAt)
        ).length,
        deliveredCount: orderedByCreated.filter(
          (delivery) => delivery.status === "delivered"
        ).length,
        deliveries: orderedByCreated,
        entitlementKeys: Array.from(
          new Set(
            orderedByCreated
              .map((delivery) => delivery.governance?.entitlementKey)
              .filter(Boolean)
          )
        ) as string[],
        exportIds: Array.from(
          new Set(orderedByCreated.map((delivery) => delivery.exportId).filter(Boolean))
        ) as string[],
        externalDeliveryIds: Array.from(
          new Set(
            orderedByCreated
              .map((delivery) => delivery.execution?.externalDeliveryId)
              .filter(Boolean)
          )
        ) as string[],
        governanceLabels: Array.from(new Set(governanceLabels)),
        id: rootId,
        latestActivityAt: getLatestActivity(latestDelivery),
        latestDelivery,
        latestStatus: latestDelivery.status,
        partnerKeys: Array.from(
          new Set(orderedByCreated.map((delivery) => delivery.partnerKey).filter(Boolean))
        ) as string[],
        pendingRetryCount: orderedByCreated.filter(
          (delivery) =>
            delivery.status === "retryable" || Boolean(delivery.execution?.nextRetryAt)
        ).length,
        posture: deriveChainPosture(orderedByCreated, latestDelivery),
        purposeLabels: Array.from(
          new Set(
            orderedByCreated
              .map((delivery) => delivery.governance?.purposeLabel)
              .filter(Boolean)
          )
        ) as string[],
        regionKeys: Array.from(
          new Set(
            orderedByCreated
              .map((delivery) => delivery.governance?.regionKey)
              .filter(Boolean)
          )
        ) as string[],
        replayCount: orderedByCreated.filter((delivery) =>
          Boolean(delivery.replayedFromDeliveryId)
        ).length,
        rootId,
      } satisfies PartnerDeliveryChainSummary
    })
    .sort(
      (left, right) =>
        new Date(right.latestActivityAt ?? 0).getTime()
        - new Date(left.latestActivityAt ?? 0).getTime()
    )
}
